import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  AppBar,
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";

import { ReactComponent as MaryIcon } from "../assets/Frame_small.svg";
import { ReactComponent as MicIcon } from "../assets/mic.svg";
import { ReactComponent as SubmitIcon } from "../assets/submit.svg";
import { ReactComponent as HomeIcon } from "../assets/home.svg";
import { ReactComponent as ProfileIcon } from "../assets/profile.svg";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ReactComponent as MicOnIcon } from "../assets/voice-search.svg";
import { ReactComponent as CloseIcon } from "../assets/close.svg";
import { ReactComponent as PencilIcon } from "../assets/PencilSimpleLine.svg";
import { ReactComponent as TrashIcon } from "../assets/TrashSimple.svg";
import { styled } from "@mui/system";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import parse from "html-react-parser"; // To render HTML responses
import PatientListDialog from "../popup/PatientListDialog";
import { cleanHTML } from "../lib/helper";
import Dashboard from "./Dashboard";
import { useLocation, useNavigate } from "react-router-dom";
import FloatingMenu from "../common/FloatingMenu";
import { getUserLocalData } from "../lib/helper";
import { useLanguage } from "../context/LanguageContext";

const MessagesContainer = styled(Box)({
  flex: 1,
  padding: "10px 24px 48px 24px",
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  gap: "10px",
  width: "100%",
});
const MessageBubble = styled(Paper)(({ isUser }) => ({
  padding: "10px 15px",
  maxWidth: "70%",
  alignSelf: isUser ? "flex-end" : "flex-start",
  backgroundColor: isUser ? "#1976D2" : "#E3F2FD",
  color: isUser ? "#fff" : "#000",
  borderRadius: "10px",
}));
const Chat = () => {
  // const { transcript, listening, resetTranscript } = useSpeechRecognition();
  const navigate = useNavigate();
  const location = useLocation();
  const chatsMessages = location?.state?.chatsMessages || [];
  const token = JSON.parse(localStorage.getItem("access_token"));
  const patient_id = localStorage.getItem("patient_id");
  const session_id = localStorage.getItem("session_id");
  const [query, setQuery] = useState("");
  const [messages, setMessages] = useState(chatsMessages);
  const [patientList, setPatientList] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const { user_name } = getUserLocalData();
  const { languageName } = useLanguage();

  // Ensure patient_id and session_id are valid (not "undefined" or null)
  const validPatientId =
    patient_id && patient_id !== "undefined" ? patient_id : null;
  const validSessionId =
    session_id && session_id !== "undefined" ? session_id : null;
  const conversationId = location?.state?.conversationId;

  const [isFetchingData, setIsFetchingData] = useState(
    conversationId ? true : false
  );

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [isUserAtBottom, setIsUserAtBottom] = useState(true); // Track user's scroll position

  // Detect if the user is at the bottom or scrolling up
  const handleScroll = () => {
    if (!messagesContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } =
      messagesContainerRef.current;

    // If user is at the bottom (or very close), set flag to true
    setIsUserAtBottom(scrollHeight - scrollTop <= clientHeight + 10);
  };

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    // Attach scroll event listener
    container.addEventListener("scroll", handleScroll);

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (isUserAtBottom && messagesContainerRef.current) {
      // Instantly jump to the bottom (no animation)
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  }, [messages]); // Runs when messages update

  useEffect(() => {
    conversationId && fetchRecentChat();
    if (!conversationId) {
      setMessages([]);
    }
  }, [conversationId]);

  const fetchRecentChat = async (e) => {
    try {
      setIsFetchingData(true);
      const response = await axios.get(
        `${process.env.REACT_APP_QUERY_URL}/conversation/${conversationId}`
      );
      setIsFetchingData(false);
      if (response?.data) {
        const filteredMessages = response.data?.filter(
          (msg) => msg.role !== "system" && msg.role !== "tool"
        );
        setMessages(filteredMessages);
        // setPatientList(response?.data?.patients);
        setPatientList(response?.data?.patient ? [].concat(response.data.patient) : []);
      }
    } catch (error) {
      setIsFetchingData(false);
      console.error("Error:", error?.response?.data?.detail);
      setMessages((prev) => [
        ...prev,
        { role: "bot", content: error?.response?.data?.detail },
      ]);
    }
  };

  const sendMessage = async (e, customMessage = undefined) => {
    const checkEmptyMessageStatus = customMessage ? false : !query.trim();
    // && !transcript.trim();
    if (checkEmptyMessageStatus) return; // Prevent empty submissions

    const userInput = customMessage
      ? customMessage
      : // : transcript.trim()
      // ? transcript
      query;
    setQuery(""); // Clear the text input
    // resetTranscript(); // Reset voice input
    setMessages([...messages, { role: "user", content: userInput }]);
    setLoading(true); // Start loading
    try {
      if (!validPatientId) {
        const response = await axios.post(
          `${process.env.REACT_APP_QUERY_URL}?query=${userInput}&access_token=${token}&language=${languageName}`
        );

        if (response?.data) {
          // setPatientList(response?.data?.patients);
          setPatientList(response?.data?.patient ? [].concat(response.data.patient) : []);
          setOpen(true);

          setResponseMessage(response?.data?.response);
        }
      } else {
        const patient_id_formatted = patient_id.replace(/^"|"$/g, "");
        let url = `${process.env.REACT_APP_QUERY_URL}?query=${userInput}&access_token=${token}&session_id=${validSessionId}&patient_id=${patient_id_formatted}&language=${languageName}`;
        if (user_name) {
          url += `&practitioner=${user_name}`;
        }
        const response = await axios.post(url);
        if (response?.data) {
          const filteredMessages = response.data?.conversation.filter(
            (msg) => msg.role !== "system" && msg.role !== "tool"
          );
          if (!validSessionId && response?.data?.session_id) {
            localStorage.setItem("session_id", response.data.session_id);
          }
          setMessages([...messages, ...filteredMessages]);
          // setPatientList(response?.data?.patients);
          setPatientList(response?.data?.patient ? [].concat(response.data.patient) : []);
        }
      }
    } catch (error) {
      console.error("Error:", error?.response?.data?.detail);
      setMessages((prev) => [
        ...prev,
        { role: "bot", content: error?.response?.data?.detail },
      ]);
    } finally {
      setLoading(false); // Stop loading
    }
  };

  const clearMessages = () => {
    setMessages([]); // Reset chat messages
  };

  const onMicClick = () => {
    navigate("/voice", { state: { isFromChatScreen: true, messages } });
    // SpeechRecognition.startListening({ continuous: false })
  };

  return (
    <>
      {open && (
        <PatientListDialog
          open={open}
          setOpen={setOpen}
          data={patientList}
          message={responseMessage}
          clearMessages={clearMessages}
        />
      )}
      <Dashboard active={"chat"}>
        <Box className="main-content" elevation={3}>
          {isFetchingData ? (
            <Box
              sx={{
                display: "flex", // Use flexbox layout
                justifyContent: "center", // Horizontally center the content
                alignItems: "center", // Vertically center the content
                height: "100vh", // Take up the full height of the viewport
              }}
              className="main-content"
            >
              <CircularProgress
                size={40}
                sx={(theme) => ({
                  color: "white",
                })}
              />
            </Box>
          ) : null}
          {!messages?.length && !isFetchingData ? (
            <>
              <Box component={"div"} className="text-container">
                <Typography
                  variant="mSize"
                  className="speak-to-mary font-w-500 white-color"
                >
                  Speaking to MARY
                </Typography>

                <Typography
                  variant="lSize"
                  className="go-ahead font-w-400 black-color-3"
                  my={2}
                >
                  Go ahead, I'm Listening
                </Typography>
                {!validPatientId && (
                  <>
                    <Typography
                      variant="fs12"
                      className="font-w-400 black-color-3"
                    >
                      {/* Search patient by name */}
                      Search patient by firstname, lastname and date of birth
                    </Typography>
                  </>
                )}
              </Box>
              <Box className="wave" component={"div"}>
                {/* <WaveIcon
                  alt="Listening animation"
                  style={{
                    marginTop: 24,
                    width: "100%",
                    height: "100%",
                    background: "none",
                  }}
                /> */}
              </Box>
            </>
          ) : null}
          {patient_id && !isFetchingData && (
            <MessagesContainer
              className="msg-container"
              ref={messagesContainerRef}
            >
              {messages.map((msg, index) => (
                <MessageBubble
                  className="chat"
                  key={index}
                  isUser={msg.role === "user"}
                >
                  {cleanHTML(msg?.content)}
                </MessageBubble>
              ))}
              {loading && (
                <MessageBubble isUser={false}>Typing...</MessageBubble>
              )}
              <div ref={messagesEndRef} />
            </MessagesContainer>
          )}
          <Box>
            {/* <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                my: "3",
                justifyContent: "space-evenly",
                width: "50%",
                margin: "auto",
              }}
            >
              <Typography variant="mSize" className="font-w-400 white-color">
                {transcript}
              </Typography>
              {transcript && (
                <CloseIcon
                  alt="stop"
                  onClick={resetTranscript}
                  cursor="pointer"
                />
              )}
            </Box> */}
            <Box component={"div"} className="chat-message-box">
              <Box
                component="form"
                elevation={0}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: 2,
                }}
                className="message-box"
                onSubmit={(e) => {
                  e.preventDefault();
                  sendMessage();
                }}
              >
                <TextField
                  multiline
                  fullWidth
                  sx={{
                    flex: 1,
                    bgcolor: "#f5f5f5",
                    p: 1,
                    px: 2,
                  }}
                  className="message-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Send a message"
                  InputProps={{
                    startAdornment: (
                      <FloatingMenu
                        className="floating-menu"
                        onClick={sendMessage}
                      />
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        {" "}
                        <IconButton
                          onClick={sendMessage}
                          sx={{
                            "&:hover": {
                              backgroundColor: "transparent",
                            },
                          }}
                        >
                          <SubmitIcon type="submit" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  variant="outlined"
                />
                <IconButton onClick={onMicClick}>
                  <MicIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Box>
      </Dashboard>
    </>
  );
};

export default Chat;